/**
 * Sovereign wire protocol: HTTP bodies and WebSocket messages between the client and the game Worker.
 *
 * Conventions
 * - Every WebSocket message has a `t` discriminator. Client requests that expect a reply carry an `id`
 *   (a short client-chosen string); the server answers with `ack` (or `preview`) carrying the same `id`.
 * - The client's first message on a new socket is `hello` with the auth token (tokens never go in URLs).
 *   The server answers with `welcome` (full view) or `error` and closes.
 * - After any state change the server sends each connected viewer a fresh `view` (the per-viewer view is
 *   small: fog of war trims it). `clock` messages are sent for clock-only changes (pause, extend).
 * - On reconnect (visibilitychange, pageshow, online) the client sends `hello` again and gets a full resync.
 * - HTTP errors use status codes plus an `ErrorBody`. WebSocket errors use `error` or a failed `ack`.
 *
 * HTTP routes (all under /api, JSON bodies):
 *   POST /api/worlds                         CreateWorldRequest   -> CreateWorldResponse
 *   POST /api/join                           JoinLookupRequest    -> JoinLookupResponse
 *   POST /api/worlds/:id/login               PinLoginRequest      -> PinLoginResponse
 *   POST /api/worlds/:id/teacher/login       TeacherLoginRequest  -> TeacherLoginResponse
 *   PUT  /api/worlds/:id/roster              RosterRequest        -> RosterResponse          (teacher)
 *   GET  /api/worlds/:id/roster                                   -> RosterResponse          (teacher)
 *   GET  /api/worlds/:id/quiz/key                                 -> QuizKeyResponse         (student)
 *   POST /api/worlds/:id/quiz                QuizUploadRequest    -> OkResponse              (student)
 *   GET  /api/worlds/:id/quiz/envelopes                           -> EnvelopesResponse       (teacher)
 *   POST /api/worlds/:id/quiz/optout         OptOutRequest        -> OkResponse              (teacher)
 *   POST /api/worlds/:id/placement           PlacementPublishRequest -> OkResponse           (teacher)
 *   POST /api/worlds/:id/students/unlock     StudentRequest       -> RosterResponse          (teacher)
 *   POST /api/worlds/:id/students/reset      StudentRequest       -> RosterResponse          (teacher: new PIN, signs out)
 *   POST /api/worlds/:id/teacher/rotate                           -> TeacherRotateResponse   (teacher: new key, signs out)
 *   POST /api/worlds/:id/teacher/projector                        -> ProjectorTokenResponse  (teacher)
 *   DELETE /api/worlds/:id/world                                  -> OkResponse              (teacher: deletes everything)
 *   GET  /api/worlds/:id/view                                     -> ClientView              (any, fallback)
 *   GET  /api/worlds/:id/ws                  (WebSocket upgrade; then `hello`)
 *   GET  /api/health                                              -> { ok: true }
 * Auth: `Authorization: Bearer <token>` on HTTP; the `hello` message on WebSockets.
 */
import type {
  ActionError,
  ChannelId,
  ChatMessage,
  ClientView,
  ClockState,
  CountryId,
  InstantAction,
  JsonWebKeyLike,
  Order,
  PlacementPublication,
  PreviewResult,
  QuizEnvelope,
  StudentId,
  TeacherCommand,
  WorldConfig,
} from './types.ts';

/** Bump when a breaking change is made to any message below. */
export const PROTOCOL_VERSION = 1;

/** Class codes use 6 characters from this alphabet (no 0/O, 1/I/L, 2/Z, 5/S, 8/B). */
export const CLASS_CODE_ALPHABET = 'ACDEFGHJKMNPQRTUVWXY34679';
export const CLASS_CODE_LENGTH = 6;
export const PIN_LENGTH = 4;
/** Wrong PIN attempts before a student is locked out, and the lockout length. */
export const PIN_MAX_ATTEMPTS = 5;
export const PIN_LOCK_MS = 5 * 60 * 1000;
/** Wrong PINs one student may get in 24 hours. After that only the teacher can unlock the student. */
export const PIN_DAILY_MAX = 10;
/** Days a world is kept after its game ends (or after its last change, for a world that never ends). */
export const RETENTION_DAYS_AFTER_END = 30;
export const RETENTION_DAYS_IDLE = 120;
/** Longest chat message accepted, in characters. */
export const CHAT_MAX_LENGTH = 500;
/** Longest journal entry accepted, in characters. */
export const JOURNAL_MAX_LENGTH = 4000;

export type ErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'wrong_pin'
  | 'pin_locked'
  | 'rate_limited'
  | 'version_mismatch'
  | 'conflict'
  | 'too_large'
  | 'phase'
  | 'invalid_action'
  | 'internal';

export interface ErrorBody {
  error: ErrorCode;
  /** Plain words that can be shown to the user. */
  message: string;
  /** For pin_locked and rate_limited. */
  retryAfterMs?: number;
  /** For invalid_action: the engine's detailed reason. */
  detail?: ActionError;
}

export interface OkResponse {
  ok: true;
}

/* ------------------------------------------------------------------------------------------------ */
/* HTTP bodies                                                                                      */
/* ------------------------------------------------------------------------------------------------ */

export interface CreateWorldRequest {
  v: number;
  className: string;
  /** The teacher browser's ECDH P-256 public key for quiz envelopes. */
  publicKeyJwk: JsonWebKeyLike;
  options?: Partial<Pick<WorldConfig, 'nNations' | 'maxTurns'>>;
}

export interface CreateWorldResponse {
  worldId: string;
  classCode: string;
  /** Secret key kept in the teacher's localStorage. */
  teacherKey: string;
  /** Shown once so the teacher can sign in on another device. */
  recoveryCode: string;
  token: string;
}

export interface JoinLookupRequest {
  classCode: string;
}

export interface JoinLookupResponse {
  worldId: string;
  className: string;
  students: Array<{ id: StudentId; name: string }>;
}

export interface PinLoginRequest {
  studentId: StudentId;
  pin: string;
}

export interface PinLoginResponse {
  token: string;
  studentId: StudentId;
  worldId: string;
  name: string;
}

export interface TeacherLoginRequest {
  /** Either the stored teacher key or the one-time recovery code. */
  teacherKey?: string;
  recoveryCode?: string;
}

export interface TeacherLoginResponse {
  token: string;
  worldId: string;
  classCode: string;
}

export interface RosterRequest {
  /** One name per line, "First L.". Existing students keep their ids and PINs. */
  names: string[];
}

export interface RosterStudent {
  id: StudentId;
  name: string;
  pin: string;
  country: CountryId | null;
  optedOut: boolean;
  quizSubmitted: boolean;
  /** Locked out after wrong PINs. `needsTeacher` is true when the daily cap was reached. */
  locked: boolean;
  needsTeacher: boolean;
}

export interface RosterResponse {
  classCode: string;
  students: RosterStudent[];
  /** Wrong PINs in this world in the last 24 hours (a high number can mean someone is guessing). */
  wrongPinsToday: number;
  /** When the world will be deleted automatically (epoch ms). */
  deleteAfter: number | null;
}

export interface StudentRequest {
  studentId: StudentId;
}

export interface TeacherRotateResponse {
  /** The new teacher key and recovery code. Every old teacher and projector sign-in stops working. */
  teacherKey: string;
  recoveryCode: string;
  token: string;
}

export interface ProjectorTokenResponse {
  /** A read-only sign-in for the projector screen. */
  token: string;
}

export interface QuizKeyResponse {
  publicKeyJwk: JsonWebKeyLike;
  /** Per-world salt for the answer hash (deleted at roster lock). */
  salt: string;
  /** False once the roster is locked. */
  open: boolean;
}

export interface QuizUploadRequest {
  /** The sealed quiz. The placement choices (mode, declines, Head of State volunteer) are sealed inside it. */
  envelope: QuizEnvelope;
}

export interface StoredEnvelope {
  studentId: StudentId;
  envelope: QuizEnvelope;
  receivedAt: number;
}

export interface EnvelopesResponse {
  envelopes: StoredEnvelope[];
  optedOut: StudentId[];
}

export interface OptOutRequest {
  studentId: StudentId;
  optedOut: boolean;
}

export interface PlacementPublishRequest {
  publication: PlacementPublication;
}

/* ------------------------------------------------------------------------------------------------ */
/* WebSocket: client -> server                                                                       */
/* ------------------------------------------------------------------------------------------------ */

export type ClientMessage =
  | { t: 'hello'; v: number; token: string; role: 'student' | 'teacher' | 'projector' }
  | { t: 'ping' }
  | { t: 'act'; id: string; action: InstantAction }
  | { t: 'teacher'; id: string; cmd: TeacherCommand }
  | { t: 'preview'; id: string; orders: Order[] }
  | { t: 'chat'; id: string; ch: ChannelId; text: string }
  | { t: 'chat_history'; ch: ChannelId; before?: number }
  | { t: 'flag'; id: string; messageId: number }
  | { t: 'journal'; id: string; turn: number; text: string };

/* ------------------------------------------------------------------------------------------------ */
/* WebSocket: server -> client                                                                       */
/* ------------------------------------------------------------------------------------------------ */

export type NoticeKind =
  | 'proposal_received'
  | 'proposal_accepted'
  | 'proposal_declined'
  | 'new_message'
  | 'intel_report'
  | 'breaking_news'
  | 'crisis'
  | 'night_falls'
  | 'dawn'
  | 'assembly_open'
  | 'assembly_result'
  | 'paused'
  | 'resumed'
  | 'signed_out'
  | 'agenda_seated'
  | 'agenda_result'
  | 'pledge_broken';

export interface FlaggedItem {
  message: ChatMessage;
  reason: 'word_list' | 'reported';
  at: number;
}

export interface JournalEntrySummary {
  studentId: StudentId;
  turn: number;
  at: number;
  length: number;
}

export type ServerMessage =
  | { t: 'welcome'; v: number; seq: number; serverTime: number; view: ClientView }
  | { t: 'view'; seq: number; serverTime: number; view: ClientView }
  | { t: 'clock'; clock: ClockState; serverTime: number }
  | { t: 'ack'; id: string; ok: true }
  | { t: 'ack'; id: string; ok: false; error: ActionError | { code: ErrorCode; message: string } }
  | { t: 'preview'; id: string; result: PreviewResult }
  | { t: 'chat'; msg: ChatMessage }
  | { t: 'chat_history'; ch: ChannelId; messages: ChatMessage[] }
  | { t: 'notice'; kind: NoticeKind; text: string; sound: string | null }
  | { t: 'flagged'; item: FlaggedItem }
  | { t: 'journal_summary'; entries: JournalEntrySummary[] }
  /** The runtime answers `{"t":"ping"}` with `{"t":"pong"}` (no server time) without waking the object. */
  | { t: 'pong'; serverTime?: number }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'bye'; reason: 'signed_out' | 'replaced' | 'world_deleted' | 'version_mismatch' };

/** Sound to play for each notice kind (ids from src/client/generated/sounds.json). No sirens anywhere. */
export const NOTICE_SOUNDS: Record<NoticeKind, string | null> = {
  proposal_received: 'notify_proposal_received',
  proposal_accepted: 'notify_proposal_accepted',
  proposal_declined: 'notify_proposal_rejected',
  new_message: 'notify_new_message',
  intel_report: 'notify_intel_report',
  breaking_news: 'sting_breaking_news',
  crisis: 'tone_crisis_alert',
  night_falls: 'sting_night_falls',
  dawn: 'sting_dawn',
  assembly_open: 'assembly_gavel',
  assembly_result: 'assembly_tally_reveal',
  paused: null,
  resumed: null,
  signed_out: null,
  agenda_seated: 'assembly_gavel',
  agenda_result: 'assembly_tally_reveal',
  pledge_broken: 'notify_proposal_rejected',
};

/** Type guard for messages arriving at the server. Checks shape only; the engine validates content. */
export function isClientMessage(value: unknown): value is ClientMessage {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { t?: unknown }).t;
  return (
    t === 'hello' ||
    t === 'ping' ||
    t === 'act' ||
    t === 'teacher' ||
    t === 'preview' ||
    t === 'chat' ||
    t === 'chat_history' ||
    t === 'flag' ||
    t === 'journal'
  );
}

/** Canonical channel id for two nations (sorted so both sides agree). */
export function pairChannel(a: CountryId, b: CountryId): ChannelId {
  return a < b ? `pair:${a}:${b}` : `pair:${b}:${a}`;
}

export function teamChannel(c: CountryId): ChannelId {
  return `team:${c}`;
}
